import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useSetAtom } from "jotai/index";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ApplicationErrors } from "../common/applicationErrors";
import { AppRoutes } from "../common/routesUtils";
import { clearBreadcrumbsStorage } from "../context/BreadcrumbsProvider";
import { clearFiltersStorage } from "../context/FiltersProvider/utils/persistence";
import { authenticationAPI, ErrorResponse, postAuthenticationAPI } from "../datasources/api";
import { SignInDTO } from "../datasources/api-client/data-contracts";
import { ApiRoutes } from "../datasources/networking/apiRoutes";
import { sendPost } from "../datasources/networking/resources";
import {
	CompleteRecoverPasswordPayload,
	InitiateRecoverPasswordPayload,
	SelectWorkspacePayload,
	SignInRequest,
} from "../datasources/requests/auth";
import { JwtDataResponse, TokenResponse, UserWorkspace } from "../datasources/responses/auth";
import { currentAuthenticationToken, currentUser } from "../state/auth";
import { userPeriodFilter } from "../state/company";
import { makeSnackbarMessage, publishSnackbarMessage } from "../state/view";
import { usePreferences } from "./usePreferences";

const group = "auth-api";

/**
 * Defines possible sign-in workflow states (sign in, select user workspace).
 */
export enum SignInWorkflowState {
	SignIn,
	Authenticated,
	UpdatePassword,
	SelectUserWorkspace,
}

/**
 * useAuthentication is the hook that exposes functionalities related to the user session,
 * sign-in and sign-out actions.
 *
 * Values returned:
 * - state;
 * - company;
 * - workspaces;
 * - permissions;
 * - authenticationToken;
 * - authenticationError;
 * - isAuthenticatingUser.
 *
 * Functions returned:
 * - signIn;
 * - signOut;
 * - resetErrors;
 * - selectUserWorkspace;
 * - cancelUserWorkspaceSelection.
 *
 * @returns values: authenticationToken, company, permissions, authenticationError, isAuthenticatingUser, workspaces, state
 *          functions: signIn, selectUserWorkspace, cancelUserWorkspaceSelection, signOut, resetErrors
 */
export const useAuthentication = () => {
	const { selectLanguage } = usePreferences();
	const { t } = useTranslation("authentication");

	const navigateTo = useNavigate();

	const client = useQueryClient();
	const publishMessage = useSetAtom(publishSnackbarMessage);

	const [userData, setUserData] = useAtom(currentUser);
	const setPeriodFilter = useSetAtom(userPeriodFilter);
	const [requireTosAcceptance, setRequireTosAcceptance] = useState<boolean>(false);
	const [authenticationToken, setAuthenticationToken] = useAtom(currentAuthenticationToken);
	const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);

	// Compute the initial state (required by Protected view container).
	const initialState =
		userData && authenticationToken ? SignInWorkflowState.Authenticated : SignInWorkflowState.SignIn;

	const [state, setState] = useState<SignInWorkflowState>(initialState);

	const clearSession = () => {
		clearFiltersStorage();
		clearBreadcrumbsStorage();
		setAuthenticationToken(undefined);
		selectLanguage(undefined);
		setPeriodFilter(undefined);
		setUserData(undefined);
		setWorkspaces([]);
		setState(SignInWorkflowState.SignIn);
	};

	const signInMutation = useMutation<SignInDTO, ErrorResponse, SignInRequest>({
		mutationKey: [group, "sign-in"],
		mutationFn: (request) => sendPost(request, { route: ApiRoutes.AuthSignIn }, authenticationAPI),
	});

	const selectUserWorkspaceMutation = useMutation<
		TokenResponse & JwtDataResponse,
		ErrorResponse,
		SelectWorkspacePayload
	>({
		mutationKey: [group, "select-workspace"],
		mutationFn: (request) =>
			sendPost(request, { route: ApiRoutes.AuthSignInSelectWorkspace }, postAuthenticationAPI),
	});

	const initiateRecoverPasswordMutation = useMutation<{}, ErrorResponse, InitiateRecoverPasswordPayload>({
		mutationKey: [group, "initiate-recover-password"],
		mutationFn: (request) => sendPost(request, { route: ApiRoutes.AuthRecoverPasswordInitiate }, authenticationAPI),
	});

	const completeRecoverPasswordMutation = useMutation<{}, ErrorResponse, CompleteRecoverPasswordPayload>({
		mutationKey: [group, "complete-recover-password"],
		mutationFn: (request) =>
			sendPost(
				request,
				{
					route: ApiRoutes.AuthRecoverPasswordComplete,
					query: { token: request.token as string },
				},
				authenticationAPI
			),
	});

	// Exported functions and variables.
	const signIn = (request: SignInRequest) => {
		clearSession();
		setRequireTosAcceptance(false);
		signInMutation.mutateAsync(request, {
			onSuccess: (response) => {
				setWorkspaces(response.workspaces);
				setAuthenticationToken(response.token);
				selectLanguage(response.preferredLanguage);
				setState(SignInWorkflowState.SelectUserWorkspace);
			},
			onError: (e: ErrorResponse) => {
				clearSession();

				switch (e.code) {
					case ApplicationErrors.AuthPasswordExpired:
						setState(SignInWorkflowState.UpdatePassword);
						setAuthenticationToken(e.temporaryToken);
						break;
					case ApplicationErrors.AuthRequiresTosAcceptance:
						setRequireTosAcceptance(true);
						break;
					default:
						break;
				}
			},
		});
	};

	const initiateRecoverPassword = (request: InitiateRecoverPasswordPayload) => {
		initiateRecoverPasswordMutation.mutateAsync(request, {
			onSuccess: () => publishMessage(makeSnackbarMessage(t("initiate recover password"))),
			onError: clearSession,
		});
	};

	const completeRecoverPassword = (request: CompleteRecoverPasswordPayload, onSuccess: () => void) => {
		completeRecoverPasswordMutation.mutateAsync(request, {
			onSuccess: () => {
				onSuccess?.();
				publishMessage(makeSnackbarMessage(t("complete recover password")));
			},
			onError: clearSession,
		});
	};

	const signOut = () => {
		// Reset cache for all queries, which also means "clear all errors".
		client
			.getQueryCache()
			.getAll()
			.forEach((q) => q.reset());

		// Clear both authentication token and JWT data.
		clearSession();
	};

	const updateLocalUserDisplayData = (firstName: string, lastName: string, email: string) => {
		// Safe to do, as userData has already been loaded when this function is called by other components.
		setUserData({ ...userData!, user: { firstName, lastName, email } });
	};

	const resetErrors = () => {
		signInMutation.reset();
		selectUserWorkspaceMutation.reset();
		initiateRecoverPasswordMutation.reset();
	};

	// forces user to sign out and go to sign-in page
	// used in case of httpCode 401
	const forceSignIn = (e: ErrorResponse) => {
		signOut();
		navigateTo(AppRoutes.SignIn);
		publishMessage(makeSnackbarMessage(t(e.message), "error"));
	};
	return {
		authenticationToken,
		user: userData?.user,
		company: userData?.company,
		availableWorkspacesCount: userData?.availableWorkspacesCount || workspaces.length || 0,
		availableWorkspaces: workspaces,
		permissions: userData?.permissions,
		authenticationError:
			signInMutation.error ??
			selectUserWorkspaceMutation.error ??
			initiateRecoverPasswordMutation.error ??
			completeRecoverPasswordMutation.error,
		isAuthenticatingUser:
			signInMutation.isPending ||
			selectUserWorkspaceMutation.isPending ||
			initiateRecoverPasswordMutation.isPending ||
			completeRecoverPasswordMutation.isPending,
		state,
		requireTosAcceptance,
		forceSignIn,
		signIn,
		signOut,
		resetErrors,
		initiateRecoverPassword,
		completeRecoverPassword,
		updateLocalUserDisplayData,
	};
};
